export type PostureFactor = {
  key: string;
  met: boolean;
  bonusPoints?: number | null;
};

export type SecurityPosture = {
  sigiloScore: number;
  sigiloLevel: string;
  riskScore?: number | null;
  riskLevel?: string | null;
  riskScoreLocked: boolean;
  factors: PostureFactor[];
};

export type AttentionItem = {
  id: string;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  isLocked: boolean;
  documentId?: string | null;
  documentName?: string | null;
  recipientId?: string | null;
  recipientName?: string | null;
  actionHref: string;
  metadata?: Record<string, unknown> | null;
};

export type DocumentPortfolio = {
  basicCount: number;
  standardCount: number;
  advancedCount: number;
  ungeneratedCount: number;
};

export type OutstandingShare = {
  documentId: string;
  documentName: string;
  recipientId: string;
  recipientName?: string | null;
  recipientEmail?: string | null;
  downloadStatus: 'pending' | 'downloaded' | 'expired';
  daysSinceSent?: number | null;
  protectionLevel: string;
  detailsLocked: boolean;
};

export type DashboardFeatureAccess = {
  riskScoreMode: 'teaser' | 'preview' | 'full' | string;
  sharesDetailLevel: 'countOnly' | 'names' | 'full' | string;
  maxAttentionItems: number;
  showPortfolioBreakdown: boolean;
  canSendReminder: boolean;
  canRequestConfirmation: boolean;
  canRequestDeletion: boolean;
};

export type DashboardInsights = {
  posture: SecurityPosture;
  attentionItems: AttentionItem[];
  portfolio: DocumentPortfolio;
  outstandingShares: OutstandingShare[];
  featureAccess: DashboardFeatureAccess;
};

function readNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && !Number.isNaN(value) ? value : fallback;
}

function readBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function normalizeDashboardInsights(raw: unknown): DashboardInsights | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const postureRaw = data.posture as Record<string, unknown> | undefined;
  if (!postureRaw) return null;

  const factors = Array.isArray(postureRaw.factors)
    ? postureRaw.factors.map((f) => {
        const item = f as Record<string, unknown>;
        return {
          key: readString(item.key),
          met: readBool(item.met),
          bonusPoints: item.bonusPoints == null ? null : readNumber(item.bonusPoints),
        };
      })
    : [];

  const attentionItems = Array.isArray(data.attentionItems)
    ? data.attentionItems.map((a) => {
        const item = a as Record<string, unknown>;
        return {
          id: readString(item.id),
          type: readString(item.type),
          severity: readString(item.severity, 'low') as AttentionItem['severity'],
          isLocked: readBool(item.isLocked),
          documentId: item.documentId != null ? readString(item.documentId) : null,
          documentName: item.documentName != null ? readString(item.documentName) : null,
          recipientId: item.recipientId != null ? readString(item.recipientId) : null,
          recipientName: item.recipientName != null ? readString(item.recipientName) : null,
          actionHref: readString(item.actionHref),
          metadata: (item.metadata as Record<string, unknown>) ?? null,
        };
      })
    : [];

  const portfolioRaw = (data.portfolio as Record<string, unknown>) ?? {};
  const shares = Array.isArray(data.outstandingShares)
    ? data.outstandingShares.map((s) => {
        const item = s as Record<string, unknown>;
        return {
          documentId: readString(item.documentId),
          documentName: readString(item.documentName),
          recipientId: readString(item.recipientId),
          recipientName: item.recipientName != null ? readString(item.recipientName) : null,
          recipientEmail: item.recipientEmail != null ? readString(item.recipientEmail) : null,
          downloadStatus: readString(item.downloadStatus, 'pending') as OutstandingShare['downloadStatus'],
          daysSinceSent: item.daysSinceSent == null ? null : readNumber(item.daysSinceSent),
          protectionLevel: readString(item.protectionLevel),
          detailsLocked: readBool(item.detailsLocked),
        };
      })
    : [];

  const accessRaw = (data.featureAccess as Record<string, unknown>) ?? {};

  return {
    posture: {
      sigiloScore: readNumber(postureRaw.sigiloScore),
      sigiloLevel: readString(postureRaw.sigiloLevel, 'low'),
      riskScore: postureRaw.riskScore == null ? null : readNumber(postureRaw.riskScore),
      riskLevel: postureRaw.riskLevel != null ? readString(postureRaw.riskLevel) : null,
      riskScoreLocked: readBool(postureRaw.riskScoreLocked, true),
      factors,
    },
    attentionItems,
    portfolio: {
      basicCount: readNumber(portfolioRaw.basicCount),
      standardCount: readNumber(portfolioRaw.standardCount),
      advancedCount: readNumber(portfolioRaw.advancedCount),
      ungeneratedCount: readNumber(portfolioRaw.ungeneratedCount),
    },
    outstandingShares: shares,
    featureAccess: {
      riskScoreMode: readString(accessRaw.riskScoreMode, 'teaser'),
      sharesDetailLevel: readString(accessRaw.sharesDetailLevel, 'countOnly'),
      maxAttentionItems: readNumber(accessRaw.maxAttentionItems, 3),
      showPortfolioBreakdown: readBool(accessRaw.showPortfolioBreakdown, true),
      canSendReminder: readBool(accessRaw.canSendReminder),
      canRequestConfirmation: readBool(accessRaw.canRequestConfirmation),
      canRequestDeletion: readBool(accessRaw.canRequestDeletion),
    },
  };
}
